import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { TrendingUp, BarChart3, Activity, Calendar, Target, Zap, MoreVertical, ChevronDown } from 'lucide-react';
import DatePickerButton from '../ui/date-picker-button';
import PerformanceTimeSeriesChart from '../charts/PerformanceTimeSeriesChart';
import PerformanceBenchmarkChart from '../charts/PerformanceBenchmarkChart';
import PerformanceHeatmap from '../charts/PerformanceHeatmap';
import { KpiNames } from '../../constants/kpi-constants';
import moment from 'moment';

const PerformanceAnalytics = () => {
  // Default date ranges
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  // 14-day window (inclusive) for non-live charts: yesterday back to 13 days prior
  const defaultStartDate = new Date(yesterday);
  defaultStartDate.setDate(defaultStartDate.getDate() - 13);
  // State for date ranges
  // Live trends: default to 1 day (yesterday to yesterday)
  const [liveStartDate, setLiveStartDate] = useState(yesterday);
  const [liveEndDate, setLiveEndDate] = useState(yesterday);
  const [benchmarkStartDate, setBenchmarkStartDate] = useState(defaultStartDate);
  const [benchmarkEndDate, setBenchmarkEndDate] = useState(yesterday);
  const [heatmapStartDate, setHeatmapStartDate] = useState(defaultStartDate);
  const [heatmapEndDate, setHeatmapEndDate] = useState(yesterday);

  // State for KPI selections
  const [selectedLiveKPI, setSelectedLiveKPI] = useState('performance_ratio');
  const [selectedBenchmarkKPI, setSelectedBenchmarkKPI] = useState('performance_ratio');
  const [selectedHeatmapKPI, setSelectedHeatmapKPI] = useState('performance_ratio');

  // State for filters
  const [benchmarkFilter, setBenchmarkFilter] = useState('Designed');
  const [numberOfDays, setNumberOfDays] = useState(15);

  // State for dropdown menus
  const [showLiveMenu, setShowLiveMenu] = useState(false);
  const [showBenchmarkMenu, setShowBenchmarkMenu] = useState(false);
  const [showHeatmapMenu, setShowHeatmapMenu] = useState(false);

  // KPI lists for dropdowns
  const specificKPINames = ['Plant Availability (%)', 'Performance Ratio (%)', 'Energy Export (kWh)', 'Grid Availability (%)', 'Tilt POA Irradiation(kWh/m^2)', 'Specific Yield (kWh/kWp)', 'Temp Corr PR (%)'];
  const kpiList = Object.keys(KpiNames)
    .filter((key) => specificKPINames.includes(KpiNames[key].name))
    .map((key) => ({ key, name: KpiNames[key].name }));

  const dayOptions = [14, 7, 3, 2];

  // Check if date is today
  const isLiveData = (endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(endDate);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate >= today;
  };

  const formatDateRange = (startDate, endDate) => {
    return `${moment(startDate).format('DD/MM/YYYY')} - ${moment(endDate).format('DD/MM/YYYY')}`;
  };

  const applyLiveFilter = () => {
    setShowLiveMenu(false);
    // Trigger chart refresh
  };

  const applyBenchmarkFilter = () => {
    setShowBenchmarkMenu(false);
    // Trigger chart refresh
  };

  const applyHeatmapFilter = () => {
    setShowHeatmapMenu(false);
    // Trigger chart refresh
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Performance Analytics
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Comprehensive performance analytics and monitoring
          </p>
        </div>
      </div>

      {/* Live Trends Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-4">
            {isLiveData(liveEndDate) ? (
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-2 h-2 bg-red-400 rounded-full animate-ping"></div>
                </div>
                <span className="text-sm font-medium text-red-600">LIVE</span>
              </div>
            ) : (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {formatDateRange(liveStartDate, liveEndDate)}
              </span>
            )}
          </div>
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowLiveMenu(!showLiveMenu)}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
            {showLiveMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border p-4 z-10">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Start Date</label>
                    <DatePickerButton
                      date={liveStartDate}
                      onDateChange={setLiveStartDate}
                      className="w-full mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">End Date</label>
                    <DatePickerButton
                      date={liveEndDate}
                      onDateChange={setLiveEndDate}
                      className="w-full mt-1"
                    />
                  </div>
                  <Button onClick={applyLiveFilter} className="w-full">
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-400 mb-4">
            Plant Performance: Live Trends
          </h3>
          <PerformanceTimeSeriesChart
            startDate={moment(liveStartDate).format('YYYY-MM-DD')}
            endDate={moment(liveEndDate).format('YYYY-MM-DD')}
            selectedKPI={selectedLiveKPI}
          />
        </CardContent>
      </Card>

      {/* Benchmark Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-4">
            {isLiveData(benchmarkEndDate) ? (
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-2 h-2 bg-red-400 rounded-full animate-ping"></div>
                </div>
                <span className="text-sm font-medium text-red-600">LIVE</span>
              </div>
            ) : (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {formatDateRange(benchmarkStartDate, benchmarkEndDate)}
              </span>
            )}
          </div>
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowBenchmarkMenu(!showBenchmarkMenu)}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
            {showBenchmarkMenu && (
              <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border p-4 z-10">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">KPI</label>
                    <div className="relative mt-1">
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                        onClick={() => {}}
                      >
                        {kpiList.find(k => k.key === selectedBenchmarkKPI)?.name || 'Select KPI'}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Filter</label>
                    <div className="flex gap-2 mt-1">
                      <Button
                        variant={benchmarkFilter === 'Designed' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setBenchmarkFilter('Designed')}
                      >
                        Designed
                      </Button>
                      <Button
                        variant={benchmarkFilter === 'Contractual' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setBenchmarkFilter('Contractual')}
                      >
                        Contractual
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Number of Days</label>
                    <div className="flex gap-2 mt-1">
                      {dayOptions.map(days => (
                        <Button
                          key={days}
                          variant={numberOfDays === days ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNumberOfDays(days)}
                        >
                          {days}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-sm font-medium">Start Date</label>
                      <DatePickerButton
                        date={benchmarkStartDate}
                        onDateChange={setBenchmarkStartDate}
                        className="w-full mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">End Date</label>
                      <DatePickerButton
                        date={benchmarkEndDate}
                        onDateChange={setBenchmarkEndDate}
                        className="w-full mt-1"
                      />
                    </div>
                  </div>
                  <Button onClick={applyBenchmarkFilter} className="w-full">
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-400 mb-4">
            Performance Benchmark: {benchmarkFilter}
          </h3>
          <PerformanceBenchmarkChart
            startDate={moment(benchmarkStartDate).format('YYYY-MM-DD')}
            endDate={moment(benchmarkEndDate).format('YYYY-MM-DD')}
            selectedKPI={selectedBenchmarkKPI}
            filter={benchmarkFilter}
          />
        </CardContent>
      </Card>

      {/* Heatmap */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-4">
            {isLiveData(heatmapEndDate) ? (
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-2 h-2 bg-red-400 rounded-full animate-ping"></div>
                </div>
                <span className="text-sm font-medium text-red-600">LIVE</span>
              </div>
            ) : (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {formatDateRange(heatmapStartDate, heatmapEndDate)}
              </span>
            )}
          </div>
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowHeatmapMenu(!showHeatmapMenu)}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
            {showHeatmapMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border p-4 z-10">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">KPI</label>
                    <div className="relative mt-1">
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                        onClick={() => {}}
                      >
                        {kpiList.find(k => k.key === selectedHeatmapKPI)?.name || 'Select KPI'}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Start Date</label>
                    <DatePickerButton
                      date={heatmapStartDate}
                      onDateChange={setHeatmapStartDate}
                      className="w-full mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">End Date</label>
                    <DatePickerButton
                      date={heatmapEndDate}
                      onDateChange={setHeatmapEndDate}
                      className="w-full mt-1"
                    />
                  </div>
                  <Button onClick={applyHeatmapFilter} className="w-full">
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-400 mb-4">
            {kpiList.find(k => k.key === selectedHeatmapKPI)?.name || 'Performance'} Heatmap
          </h3>
          <PerformanceHeatmap
            startDate={moment(heatmapStartDate).format('YYYY-MM-DD')}
            endDate={moment(heatmapEndDate).format('YYYY-MM-DD')}
            selectedKPI={selectedHeatmapKPI}
          />
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Performance Ratio
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  86.39%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Activity className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Plant Availability
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  99.94%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                <Zap className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Energy Export
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  3,076,100 kWh
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <Target className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Specific Yield
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  1,234 kWh/kWp
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PerformanceAnalytics;